import { GridGroupNode, useGridApiRef } from '@mui/x-data-grid-pro'
import { useCallback, useEffect, useRef, useState } from 'react'

import { AlertState, CatalogData, convertToDataTree, DialogState, TreeItem } from '../definitions'

/**
 * Custom hook for managing a catalog of items.
 *
 * @param initialData - data to initialize the catalog.
 * @returns An object with methods and state for managing the catalog.
 */
export const useCatalog = (initialData: CatalogData = []) => {
  const [nodes, setNodes] = useState<TreeItem[]>(convertToDataTree(initialData))

  const [deleteDialogState, setDeleteDialogState] = useState<DialogState>({ open: false, itemId: undefined })
  const [editDialogState, setEditDialogState] = useState<DialogState>({ open: false, itemId: undefined })
  const [alertState, setAlertState] = useState<AlertState>({ message: '', visible: false })

  const apiRef = useGridApiRef()

  /**
   * Ref for maintaining the expanded state of nodes.
   */
  const expandedState = useRef<{ [key: string]: boolean }>({})

  /**
   * Removes an item from the catalog by its ID and updates the state.
   *
   * @param itemId - The ID of the item to be removed.
   * @param result - The result array for recursion.
   */
  const removeItemById = useCallback(
    (itemId: number | string, result: TreeItem[] = [...nodes]): boolean => {
      try {
        const indexToRemove = result.findIndex((item) => item.id === itemId)

        if (indexToRemove !== -1) {
          const pathToRemove = result[indexToRemove].path
          const itemsToRemove = result.filter((item) => item.path.startsWith(pathToRemove))

          itemsToRemove.forEach((item) => {
            const itemIndex = result.findIndex((i) => i.id === item.id)

            result.splice(itemIndex, 1)
          })
        }

        setNodes(result)
        return true
      } catch (e) {
        return false
      }
    },
    [nodes],
  )

  /**
   * Inserts a new item into the catalog at the correct position and updates the state.
   *
   * @param itemPath - The path of the new item.
   * @param description - The description of the new item.
   * @param positionPath - The position path for insertion, used to dictate the position of the new item.
   */
  const insertNewItem = useCallback(
    (itemPath: string, description: string, positionPath = ''): boolean => {
      const result = [...nodes]

      const newItem: TreeItem = {
        path: positionPath ? positionPath + '/' + itemPath : itemPath,
        description,
        id: crypto.randomUUID(),
      }

      try {
        let indexToInsert = result.findIndex((item) => item.path > newItem.path)

        if (indexToInsert === -1) {
          // If no greater path is found, insert at the end
          indexToInsert = result.length
        }

        result.splice(indexToInsert, 0, newItem)

        setNodes(result)

        return true
      } catch (e) {
        return false
      }
    },
    [nodes],
  )

  /**
   * Edits the path and/or description of an item in the catalog by its id and updates the state.
   *
   * @param id - The id of the item to edit.
   * @param path - The path of the item to edit.
   * @param description - The description of the item.
   */
  const editItem = useCallback(
    (id: number | string, path: string, description: string, result: TreeItem[] = [...nodes]): boolean => {
      const res = [...result]

      try {
        const itemToEdit = res.find((item) => item.id === id)

        if (itemToEdit !== undefined) {
          const oldPath = itemToEdit.path

          if (description !== itemToEdit.description) {
            itemToEdit.description = description
          }

          if (path === oldPath) {
            return true
          }

          // Update the properties of the item
          itemToEdit.path = path

          // If the path changes, update the paths of children
          if (oldPath !== path) {
            // Update paths of direct children
            res.forEach((item) => {
              if (item.path.startsWith(oldPath) && item.id !== id) {
                item.path = path + item.path.slice(oldPath.length)
              }
            })

            // Update paths of indirect children
            res.forEach((item) => {
              if (item.path.startsWith(oldPath) && item.id !== id) {
                const childPath = item.path.slice(path.length)
                item.path = path + childPath
              }
            })
          }
        }

        setNodes(res)

        return true
      } catch (e) {
        return false
      }
    },
    [nodes],
  )

  /**
   * @returns A value/label array representation of the nodes.
   */
  function getNodesLabel(): { value: string; label: string }[] {
    if (!nodes.length) {
      return []
    }
    const parsed = nodes.map((el) => {
      return { label: el.path.split('/').pop() || '', value: el.path }
    })

    return [{ label: 'None', value: '' }, ...parsed]
  }

  /**
   * Subscribes to the 'rowExpansionChange' event in the Grid API
   * and updates the expanded state in {@link expandedState} onChange.
   */
  useEffect(() => {
    // The `subscribeEvent` method will automatically unsubscribe in the cleanup function of the `useEffect`.
    return apiRef.current.subscribeEvent('rowExpansionChange', (node) => {
      expandedState.current[node.id] = node.childrenExpanded ?? false
    })
  }, [apiRef])

  /**
   * Synchronizes {@link expandedState} with the nodes in the catalog as CRUD actions are performed.
   */
  useEffect(() => {
    const initialObject: { [key: string]: boolean } = {}
    nodes.forEach((item) => {
      if (expandedState.current[item.id]) initialObject[item.id] = expandedState.current[item.id]
      else initialObject[item.id] = false
    })
    expandedState.current = initialObject
  }, [nodes])

  /**
   * Checks if a group is expanded based on its node ID.
   *
   * @param node - The GridGroupNode to check.
   * @returns True if the group is expanded, false otherwise. Based on the {@link expandedState} ref.
   */
  const isGroupExpanded = useCallback((node: GridGroupNode) => expandedState.current[node.id] ?? false, [expandedState])

  /**
   * <-- Edit Dialog -->
   */

  /**
   * Opens the EditDialog to allow the user to change the item information with its id.
   * @param itemId The id assigned to the item.
   */
  const openEditDialog = useCallback((itemId?: string | number | undefined) => {
    if (!itemId) {
      setEditDialogState({
        itemId: undefined,
        open: true,
      })
    } else {
      setEditDialogState({
        open: true,
        itemId,
      })
    }
  }, [])

  /**
   * Closes the EditDialog.
   */
  const closeEditDialog = useCallback(() => {
    setEditDialogState({ open: false, itemId: undefined })
  }, [])

  /**
   * <-- Delete Dialog -->
   */

  /**
   * Opens the DeleteDialog to allow the user to delete the item with its id.
   * @param itemId The id assigned to the item.
   */
  const openDeleteDialog = useCallback((itemId: string | number) => {
    if (!itemId) {
      return
    }
    setDeleteDialogState({
      open: true,
      itemId,
    })
  }, [])

  /**
   * Closes the DeleteDialog.
   */
  const closeDeleteDialog = useCallback(() => setDeleteDialogState({ open: false, itemId: undefined }), [])

  /**
   * <-- Success Alert -->
   */

  /**
   * Shows the Alert to inform the user about the state of an executed operation.
   * @param message The message to show to the user.
   */
  const openAlert = useCallback((message: string) => {
    setAlertState({
      message,
      visible: true,
    })
  }, [])

  /**
   * Hides the Alert.
   */
  const closeAlert = useCallback(
    () =>
      setAlertState({
        message: '',
        visible: false,
      }),
    [],
  )

  /**
   * Automatically hides the Alert after 6 seconds.
   */
  useEffect(() => {
    if (alertState.visible) {
      setTimeout(() => closeAlert(), 6000)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alertState.visible])

  return {
    nodes,
    apiRef,
    getNodesLabel,
    isGroupExpanded,
    removeItemById,
    insertNewItem,
    editItem,
    deleteDialogState,
    editDialogState,
    alertState,
    openDeleteDialog,
    openEditDialog,
    openAlert,
    closeDeleteDialog,
    closeEditDialog,
    closeAlert,
  }
}
